import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, CircularProgress, IconButton, Stack,
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Alert, Avatar, Divider, Drawer, Menu, MenuItem,
    ListItemIcon, DialogContentText, Snackbar, Select, FormControl, InputLabel
} from '@mui/material';
import adminService from '../services/admin.service';

import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppBadIcon from '@mui/icons-material/GppBad';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';


const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts[0] && nameParts[0].length > 1) {
        return nameParts[0].substring(0, 2).toUpperCase();
    }
    return (name[0] || '').toUpperCase();
};

const modalTextFieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB',
        },
    },
    '& label.Mui-focused': {
        color: '#2563EB',
    },
};

const AdminUserDetailDrawer = ({ user, open, onClose, onUserDeleted, onUserUpdated }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isEditUserModalOpen, setEditUserModalOpen] = useState(false);

    const [editingUserData, setEditingUserData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (user) {
            setEditingUserData({ ...user });
        }
    }, [user]);


    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenEditUserModal = () => {
        setEditUserModalOpen(true);
        handleMenuClose();
    };

    const handleCloseEditUserModal = () => {
        setEditUserModalOpen(false);
    };

    const handleEditUserSubmit = async () => {
        if (!editingUserData) return;
        try {
            const { id, ...dataToSubmit } = editingUserData;
            const updatedUser = await adminService.updateUser(id, dataToSubmit);
            if (onUserUpdated) {
                onUserUpdated(updatedUser.data);
            }
            handleCloseEditUserModal();
            setSnackbar({ open: true, message: 'User updated successfully.', severity: 'success' });
        } catch (error) {
            console.error("Failed to update user:", error);
            setSnackbar({ open: true, message: 'Failed to update user.', severity: 'error' });
        }
    };

    const handleDeleteUser = async () => {
        if (!user) return;
        try {
            await adminService.deleteUser(user.id);
            if (onUserDeleted) {
                onUserDeleted(user.id);
            }
            onClose();
        } catch (error) {
            console.error("Failed to delete user:", error);
            setSnackbar({ open: true, message: 'Failed to delete user.', severity: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            handleMenuClose();
        }
    };

    const drawerContent = (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh', width: { xs: '100vw', sm: 500, md: 600 } }}>
            {!user ? (
                <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />
            ) : (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                            <IconButton onClick={onClose}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Avatar sx={{ bgcolor: '#2563EB', color: '#ffffff', width: 50, height: 50, fontSize: '1.2rem' }}>
                                {getInitials(user.username)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user.username}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2563EB' }}>
                                    {user.company || 'No Company'}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={openMenu ? 'long-menu' : undefined}
                            aria-expanded={openMenu ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={handleMenuClick}
                            sx={{ color: 'grey.700' }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Stack>

                    <Paper sx={{ p: 3, borderRadius: 4, bgcolor: 'background.paper', boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>User Details</Typography>
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">{user.company || 'Not specified'}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                {user.is_verified ? <VerifiedUserIcon color="success" /> : <GppBadIcon color="disabled" />}
                                <Typography variant="body2">{user.is_verified ? "Verified" : "Not Verified"}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                {user.role === 'admin' ? <AdminPanelSettingsIcon color="primary" /> : <PersonIcon />}
                                <Typography variant="body2">{user.role}</Typography>
                            </Stack>
                        </Stack>
                    </Paper>


                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        keepMounted={false}
                        disableRestoreFocus={true}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                borderRadius: '16px',
                                p: 1,
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                    >
                        <MenuItem onClick={handleOpenEditUserModal}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            Edit User
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                            </ListItemIcon>
                            Delete User
                        </MenuItem>
                    </Menu>
                </>
            )}

            {editingUserData && (
                <Dialog
                    open={isEditUserModalOpen}
                    onClose={handleCloseEditUserModal}
                    fullWidth
                    maxWidth="sm"
                    keepMounted={false}
                    disableRestoreFocus={true}
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>Edit User</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField label="Username" name="username" value={editingUserData.username} onChange={(e) => setEditingUserData({ ...editingUserData, username: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <TextField label="Company" name="company" value={editingUserData.company} onChange={(e) => setEditingUserData({ ...editingUserData, company: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <TextField label="Email" name="email" value={editingUserData.email} onChange={(e) => setEditingUserData({ ...editingUserData, email: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <FormControl fullWidth sx={modalTextFieldStyle}>
                                <InputLabel id="role-select-label">Role</InputLabel>
                                <Select
                                    labelId="role-select-label"
                                    id="role-select"
                                    value={editingUserData.role}
                                    label="Role"
                                    onChange={(e) => setEditingUserData({ ...editingUserData, role: e.target.value })}
                                >
                                    <MenuItem value="user">User</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCloseEditUserModal} sx={{ color: 'grey.700', borderRadius: '12px' }}>Cancel</Button>
                        <Button onClick={handleEditUserSubmit} variant="contained" sx={{ bgcolor: '#2563EB', color: 'white', borderRadius: '12px', boxShadow: 'none', '&:hover': { bgcolor: '#1D4ED8', boxShadow: 'none' } }}>Save Changes</Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                keepMounted={false}
                disableRestoreFocus={true}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    {"Are you sure you want to delete this user?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        This action is permanent and cannot be undone. All data associated with this user will be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'grey.700', borderRadius: '12px' }}>Cancel</Button>
                    <Button onClick={handleDeleteUser} autoFocus color="error" variant="contained" sx={{ borderRadius: '12px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                keepMounted={false}
                disableRestoreFocus={true}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            keepMounted={false}
            disableRestoreFocus={true}
        >
            {drawerContent}
        </Drawer>
    );
};

export default AdminUserDetailDrawer; 